import { formatEasternDeadline } from '../lib/dateUtils'
import { Check } from 'lucide-react'

/**
 * TournamentHero - Hero section for tournament overview page (user view)
 * Displays tournament info with engagement stats
 *
 * @param {object} tournament - Tournament data
 * @param {number} bracketCount - Number of brackets in the tournament
 * @param {number} entryCount - Number of brackets the user has entered
 * @param {number} totalBrackets - Total brackets available
 */
export default function TournamentHero({ tournament, bracketCount = 0, entryCount = 0, totalBrackets = 0 }) {
  if (!tournament) return null

  const formatDateRange = (startDate, endDate) => {
    if (!startDate || !endDate) return ''

    const parseDate = (date) => {
      if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
        const [year, month, day] = date.split('-').map(Number)
        return new Date(year, month - 1, day)
      }
      return new Date(date)
    }

    const start = parseDate(startDate)
    const end = parseDate(endDate)

    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ]

    const startMonth = months[start.getMonth()]
    const startDay = start.getDate()
    const endMonth = months[end.getMonth()]
    const endDay = end.getDate()
    const year = end.getFullYear()

    if (startMonth === endMonth) {
      return `${startMonth} ${startDay} - ${endDay}, ${year}`
    } else {
      return `${startMonth} ${startDay} - ${endMonth} ${endDay}, ${year}`
    }
  }

  // Check if tournament is open for picks
  const now = new Date()

  // Determine active deadline (check open round in divisions/brackets first, then tournament level)
  let activeDeadline = tournament.submission_deadline || null
  if (Array.isArray(tournament.divisions)) {
    for (const division of tournament.divisions) {
      if (Array.isArray(division.rounds)) {
        const openRound = division.rounds.find(r => r.status === 'open')
        if (openRound && openRound.submission_deadline) {
          activeDeadline = openRound.submission_deadline
          break
        }
      }
    }
  }

  const deadline = activeDeadline ? new Date(activeDeadline) : null
  const isOpen = deadline && now < deadline
  const closesAt = formatEasternDeadline(activeDeadline)

  return (
    <div className="mb-8">
      {/* Tournament Info */}
      <div className="card-glass-item card-glass-item-no-lift card-glass-item-no-glow rounded-lg px-6 py-6">
        {/* Submission Deadline (if open) */}
        {isOpen && closesAt && (
          <div className="mb-6 px-4 py-3 rounded-lg bg-[var(--orange-3)] border border-[var(--orange-6)] text-[var(--orange-12)] font-semibold text-base">
            Picks close at {closesAt}
          </div>
        )}

        {/* Sport Name */}
        <div className="mb-1">
          <span className="text-xs uppercase tracking-wide font-medium text-muted-foreground">
            {tournament.sport_name || 'Pickleball'}
          </span>
        </div>

        {/* Tournament Name */}
        <h1 className="text-4xl sm:text-5xl font-bold mb-1">
          {tournament.name}
        </h1>

        {/* Date Range */}
        <p className="text-base text-muted-foreground">
          {formatDateRange(tournament.start_date, tournament.end_date)}
        </p>

        {/* Engagement Stats */}
        {totalBrackets > 0 && (
          <div className="flex items-center gap-4 mt-4 text-sm text-muted-foreground">
            <span className="flex items-center gap-1.5">
              <Check className="h-3.5 w-3.5" />
              {entryCount} of {totalBrackets} brackets entered
            </span>
          </div>
        )}
      </div>
    </div>
  )
}
